import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  FileText,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Eye,
  Clock,
  CheckCircle,
  AlertCircle,
  Upload,
  Trash2,
  FileJson,
  X,
  File,
} from "lucide-react";
import { sipGetWorkOrders, sipGetSupportedFunds, sipGetStats } from "../api";
import "./SIPWorkOrdersList.css";

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUploadComplete, supportedFunds }) => {
  const [files, setFiles] = useState([]);
  const [fund, setFund] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => f.name.toLowerCase().match(/\.(pdf|tiff?|jpe?g|png)$/i));
    const existingNames = files.map(f => f.name);
    const uniqueFiles = validFiles.filter(f => !existingNames.includes(f.name));
    
    if (validFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, JPG, and PNG files are supported.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    const newStatus = {};
    uniqueFiles.forEach(f => { newStatus[f.name] = 'pending'; });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0 || !fund) {
      alert('Please select files and a fund');
      return;
    }
    
    setUploading(true);
    const results = [];
    const token = localStorage.getItem("token");

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("fund", fund);
        
        const res = await fetch("/sip/api/upload", {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        
        const data = await res.json();
        if (res.ok) {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
          results.push({ filename: file.name, success: true, execution_id: data.execution_id });
        } else {
          setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
          results.push({ filename: file.name, success: false, error: data.message || "Upload failed" });
        }
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    if (onUploadComplete) onUploadComplete(results);
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    setFund("");
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading': return <RefreshCw size={16} className="sip-spin" style={{ color: '#3b82f6' }} />;
      case 'success': return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error': return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default: return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="sip-modal-overlay" onClick={handleClose}>
      <div className="sip-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="sip-modal-header">
          <h3><Upload size={20} /> Upload & Process SIP Form</h3>
          <button className="sip-modal-close" onClick={handleClose}><X size={20} /></button>
        </div>

        <div className="sip-modal-body">
          <div className="sip-form-select">
            <label>Fund Code *</label>
            <select value={fund} onChange={(e) => setFund(e.target.value)} disabled={uploading}>
              <option value="">Select Fund...</option>
              {Object.entries(supportedFunds).map(([code, name]) => (
                <option key={code} value={code}>{code} - {name}</option>
              ))}
            </select>
          </div>

          <div
            ref={dropZoneRef}
            className="sip-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} />
            <p>Drag & drop files here</p>
            <span>or click to browse (PDF, TIFF, JPG, PNG)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {files.length > 0 && (
            <div className="sip-file-list">
              <div className="sip-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="sip-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="sip-file-items">
                {files.map(file => (
                  <div key={file.name} className={`sip-file-item ${uploadStatus[file.name]}`}>
                    <div className="sip-file-info">
                      <File size={16} />
                      <span className="sip-file-name" title={file.name}>
                        {file.name.length > 35 ? file.name.substring(0, 35) + '...' : file.name}
                      </span>
                      <span className="sip-file-size">{(file.size / 1024 / 1024).toFixed(2)} MB</span>
                    </div>
                    <div className="sip-file-actions">
                      {getStatusIcon(uploadStatus[file.name])}
                      {uploadStatus[file.name] === 'pending' && !uploading && (
                        <button className="sip-file-remove" onClick={() => removeFile(file.name)}>
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="sip-modal-footer">
          <button className="sip-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="sip-btn-upload-all"
              onClick={handleUploadAll}
              disabled={files.length === 0 || !fund || uploading}
            >
              {uploading ? (
                <><RefreshCw size={16} className="sip-spin" /> Processing...</>
              ) : (
                <><Upload size={16} /> Upload {files.length} File{files.length !== 1 ? 's' : ''}</>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const SIPWorkOrdersList = () => {
  const navigate = useNavigate();
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(20);
  
  // Stats
  const [stats, setStats] = useState({ total: 0, completed: 0, processing: 0, failed: 0 });
  
  // Filters
  const [fundFilter, setFundFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [supportedFunds, setSupportedFunds] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Debounce timer
  const searchTimerRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    searchTimerRef.current = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(0); // Reset to first page on search
    }, 400);
    
    return () => {
      if (searchTimerRef.current) {
        clearTimeout(searchTimerRef.current);
      }
    };
  }, [searchQuery]);

  const fetchWorkOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        skip: page * pageSize,
        limit: pageSize,
      };
      
      if (fundFilter) params.fund = fundFilter;
      if (statusFilter) params.success = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;
      
      console.log('[SIP] Fetching work orders with params:', params);
      
      const data = await sipGetWorkOrders(params);
      console.log('[SIP] Received:', data?.work_orders?.length, 'work orders, total:', data?.total, 'search:', data?.search);
      
      setWorkOrders(data?.work_orders || []);
      setTotal(data?.total || 0);
    } catch (err) {
      console.error("Failed to fetch work orders:", err);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, fundFilter, statusFilter, debouncedSearch]);

  const fetchStats = async () => {
    try {
      const data = await sipGetStats();
      setStats({
        total: data?.total_work_orders || 0,
        completed: data?.by_status?.completed || 0,
        processing: data?.by_status?.processing || 0,
        failed: data?.by_status?.failed || 0,
      });
    } catch { /* ignore */ }
  };

  useEffect(() => {
    fetchWorkOrders();
    fetchStats();
  }, [fetchWorkOrders]);

  useEffect(() => {
    sipGetSupportedFunds()
      .then(data => setSupportedFunds(data?.funds || {}))
      .catch(() => {});
  }, []);

  const handleRefresh = () => {
    fetchWorkOrders();
    fetchStats();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleFilterChange = () => {
    setPage(0);
  };

  const handleDelete = async (wo) => {
    if (!window.confirm(`Delete work order ${wo.execution_id || wo._id}?`)) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/sip/api/work-orders/${wo.execution_id || wo._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        handleRefresh();
      } else {
        alert("Failed to delete work order");
      }
    } catch (err) {
      alert("Error: " + err.message);
    }
  };

  const handleDownloadJson = async (wo) => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`/sip/api/work-orders/${wo.execution_id || wo._id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${wo.barcode || wo.execution_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download: " + err.message);
    }
  };

  const getStatusIcon = (wo) => {
    if (wo.success === true) return <CheckCircle size={16} className="sip-status-icon success" />;
    if (wo.success === false) return <AlertCircle size={16} className="sip-status-icon failed" />;
    return <Clock size={16} className="sip-status-icon pending" />;
  };

  const getStatusBadge = (wo) => {
    if (wo.success === true) return <span className="sip-wo-status success">Success</span>;
    if (wo.success === false) return <span className="sip-wo-status failed">Failed</span>;
    return <span className="sip-wo-status pending">Pending</span>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  return (
    <div className="sip-work-orders-list">
      {/* Header Banner */}
      <div className="sip-header-banner">
        <div className="sip-header-info">
          <h2><FileText size={24} /> SIP Work Orders</h2>
          <p>Process and manage SIP OCR extraction requests</p>
        </div>
        <button className="sip-btn-upload" onClick={() => setShowUploadModal(true)}>
          <Upload size={18} /> Upload & Process
        </button>
      </div>

      {/* Stats Grid */}
      <div className="sip-stats-grid">
        <div className="sip-stat-card">
          <div className="sip-stat-value">{stats.total}</div>
          <div className="sip-stat-label">Total Work Orders</div>
        </div>
        <div className="sip-stat-card completed">
          <div className="sip-stat-value">{stats.completed}</div>
          <div className="sip-stat-label">Completed</div>
        </div>
        <div className="sip-stat-card processing">
          <div className="sip-stat-value">{stats.processing}</div>
          <div className="sip-stat-label">Processing</div>
        </div>
        <div className="sip-stat-card failed">
          <div className="sip-stat-value">{stats.failed}</div>
          <div className="sip-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="sip-filters-row">
        <div className="sip-search-wrapper">
          <Search size={18} />
          <input
            type="text"
            className="sip-search-input"
            placeholder="Search by barcode or execution ID..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
        </div>
        
        <div className="sip-filter-group">
          <Filter size={16} />
          <select value={fundFilter} onChange={(e) => { setFundFilter(e.target.value); handleFilterChange(); }}>
            <option value="">All Funds</option>
            {Object.entries(supportedFunds).map(([code, name]) => (
              <option key={code} value={code}>{code} - {name}</option>
            ))}
          </select>
        </div>

        <div className="sip-filter-group">
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); handleFilterChange(); }}>
            <option value="">All Status</option>
            <option value="true">Success</option>
            <option value="false">Failed</option>
          </select>
        </div>

        <button className="sip-btn-refresh" onClick={handleRefresh} disabled={loading}>
          <RefreshCw size={16} className={loading ? "sip-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="sip-table-container">
        {loading ? (
          <div className="sip-table-loading">
            <RefreshCw size={24} className="sip-spin" />
            <span>Loading work orders...</span>
          </div>
        ) : workOrders.length === 0 ? (
          <div className="sip-table-empty">
            <FileText size={48} />
            <p>No work orders found</p>
            <span>Try adjusting your filters or process a new SIP OCR request</span>
          </div>
        ) : (
          <table className="sip-table">
            <thead>
              <tr>
                <th>Execution ID</th>
                <th>Fund</th>
                <th>Barcode</th>
                <th>Transaction Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {workOrders.map((wo) => (
                <tr key={wo._id || wo.execution_id} onClick={() => navigate(`/sip/work-order/${wo.execution_id || wo._id}`)}>
                  <td>
                    <div className="sip-id-cell">
                      {getStatusIcon(wo)}
                      <code>{wo.execution_id || wo._id}</code>
                    </div>
                  </td>
                  <td><span className="sip-fund-badge">{wo.fund || "-"}</span></td>
                  <td>{wo.barcode || "-"}</td>
                  <td>{wo.transaction_type || wo.trtype || "-"}</td>
                  <td>{getStatusBadge(wo)}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td>
                    <div className="sip-actions-cell" onClick={(e) => e.stopPropagation()}>
                      <button className="sip-action-btn view" onClick={() => navigate(`/sip/work-order/${wo.execution_id || wo._id}`)} title="View">
                        <Eye size={16} />
                      </button>
                      <button className="sip-action-btn json" onClick={() => handleDownloadJson(wo)} title="Download JSON">
                        <FileJson size={16} />
                      </button>
                      <button className="sip-action-btn delete" onClick={() => handleDelete(wo)} title="Delete">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="sip-pagination">
          <div className="sip-pagination-info">
            Showing {page * pageSize + 1} - {Math.min((page + 1) * pageSize, total)} of {total}
          </div>
          <div className="sip-pagination-controls">
            <button onClick={() => handlePageChange(page - 1)} disabled={page === 0}>
              <ChevronLeft size={16} /> Previous
            </button>
            <span className="sip-page-indicator">Page {page + 1} of {totalPages}</span>
            <button onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages - 1}>
              Next <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={() => handleRefresh()}
        supportedFunds={supportedFunds}
      />
    </div>
  );
};

export default SIPWorkOrdersList;
